'use client';
import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import ProgressOverview from './components/ProgressOverview';
import SavedSessions from './components/SavedSessions';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import RecentTests from './components/RecentTests';
import QuickStart from './components/QuickStart';
import StudentAnalyticsReports from './components/StudentAnalyticsReports';
import { useAuth } from '@/contexts/AuthContext';
import { analyticsService } from '@/lib/services/analyticsService';
import { useRouter } from 'next/navigation';

interface CategoryPerformance {
  id: string;
  name: string;
  accuracy: number;
  color: string;
}

export default function StudentDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [analytics, setAnalytics] = useState<CategoryPerformance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/sign-up-login');
    }
  }, [user, authLoading, router]);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user]);

  const loadAnalytics = async () => {
    if (!user) return;

    setLoading(true);
    const data = await analyticsService.getUserAnalytics(user.id);

    const colors = ['bg-primary', 'bg-accent', 'bg-success', 'bg-warning', 'bg-primary', 'bg-accent'];
    const formattedData = data.map((item, index) => ({
      id: item.categoryId,
      name: item.categoryName,
      accuracy: Math.round(item.accuracy),
      color: colors[index % colors.length],
    }));

    setAnalytics(formattedData);
    setLoading(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow bg-muted flex items-center justify-center">
          <div className="text-center text-muted-foreground">Loading...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow bg-muted">
        <div className="container px-6 py-12 space-y-8">
          <ProgressOverview />
          <QuickStart />
          <SavedSessions />
          <PerformanceAnalytics categories={analytics} />
          <StudentAnalyticsReports />
          <RecentTests />
        </div>
      </main>
      <Footer />
    </div>
  );
}